import TypographyPanel from './TypographyPanel.vue';
import BadgePanel from './BadgePanel.vue';
import BadgeLibraryPanel from './BadgeLibraryPanel.vue';
import { t } from './i18n';
import { badgeFontsReady, buildItemSvg, fitBoxPatch, measureItem, renderItem, typographyFontReady } from './engine';
import { ensureItemFonts, itemFaceSupport } from './fontEngine';
import { normalizeBadge, normalizeTypography } from './config';
import { setTypographyBrandPalette, setTypographyPolicy, setTypographyPresets, typographyPolicy } from './policy';
import { syncTypographyColorPatch } from './ux';
import type { TypographyDesignItem } from './types';
import type { VeyopRuntime } from '@core/veyopRuntime';

type Runtime = VeyopRuntime;
let registered = false;
let unregisterBadgeLibrary: (() => void) | null = null;

function ensureDesignerStyles(): void {
  const selector = 'link[href*="/typography-pro/dist/typography-pro.bundle.css"]';
  if (document.querySelector(selector)) return;
  const entrySrc = document.querySelector<HTMLScriptElement>('script[src*="/typography-pro/dist/typography-pro.bundle.js"]')?.src;
  if (!entrySrc) return;
  const entryUrl = new URL(entrySrc);
  const styleUrl = new URL('typography-pro.bundle.css', entryUrl);
  styleUrl.search = entryUrl.search;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = styleUrl.href;
  link.dataset.veyopTypographyProStyle = '1';
  document.head.appendChild(link);
}

function hasPro(item: TypographyDesignItem): boolean { return Boolean(item?.typography || item?.typographyBadge); }

function register(Veyop: Runtime): void {
  if (registered || !Veyop.itemRenderers?.register || !Veyop.panels?.register) return;
  registered = true;
  Veyop.itemRenderers.register({ id: 'typography-pro', priority: 30, matches: (item) => hasPro(item as TypographyDesignItem), render: renderItem as never });
  Veyop.itemBounds?.register({
    id: 'typography-pro', priority: 30, matches: (item) => hasPro(item as TypographyDesignItem),
    measure: (item) => measureItem(item as TypographyDesignItem),
    fitToBox: (item, box) => fitBoxPatch(item as TypographyDesignItem, box),
  });
  Veyop.hooks?.addFilter?.('veyop.item.patch', 'typography-pro', (patch, item) =>
    syncTypographyColorPatch(patch as Record<string, unknown>, item as TypographyDesignItem));
  Veyop.textStyles?.register({
    id: 'typography-pro', priority: 30, matches: (item) => Boolean((item as TypographyDesignItem)?.typography) && !(item as TypographyDesignItem).typographyBadge,
    supports: (item) => itemFaceSupport(item as TypographyDesignItem),
  });
  Veyop.imagePipeline?.registerItemSvgExporter({
    id: 'typography-pro', priority: 30, matches: (item) => hasPro(item as TypographyDesignItem),
    // `buildItemSvg` is synchronous and yields '' until the glyph outlines are parsed, so
    // an un-primed export (headless mockup, cart thumb) would drop the layer entirely.
    prepare: (item) => ensureItemFonts(item as TypographyDesignItem),
    exportSvg: (item) => buildItemSvg(item as TypographyDesignItem),
  });
  Veyop.panels.register({
    id: 'typography-pro-text', label: t('Typography Pro'), placement: 'text-effects', description: t('Curved text, ready-made styles, and print-safe effects'), replaces: ['text-arc'], priority: 30,
    preview: '<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><text x="20" y="27" text-anchor="middle" font-family="Georgia, serif" font-size="19" font-weight="700" fill="none" stroke="currentColor" stroke-opacity=".35" stroke-width="3" stroke-linejoin="round">Aa</text><text x="20" y="27" text-anchor="middle" font-family="Georgia, serif" font-size="19" font-weight="700" fill="currentColor">Aa</text></svg>',
    isActive: (item) => Boolean((item as TypographyDesignItem | null)?.typography),
    matches: (item) => item?.type === 'text' && typographyPolicy.value.enabled && typographyPolicy.value.allowedLayouts.length > 0, render: (ctx) => ({ component: TypographyPanel, props: { item: ctx.item, update: ctx.update } }),
  });
  Veyop.panels.register({
    id: 'typography-badge', label: t('Smart Badge'), description: t('Edit badge words, shape, and style'), priority: 30,
    matches: (item) => typographyPolicy.value.enabled && Boolean((item as TypographyDesignItem | null)?.typographyBadge), render: (ctx) => ({ component: BadgePanel, props: { item: ctx.item, update: ctx.update } }),
  });
  Veyop.preflight?.registerRule({
    id: 'typography-font-unavailable', severity: 'error', message: t('Typography Pro font is not ready for vector output.'),
    matches: (item) => {
      const typed = item as TypographyDesignItem;
      if (!hasPro(typed)) return false;
      return typed.typographyBadge ? !badgeFontsReady(typed) : !typographyFontReady(typed);
    },
  });
  Veyop.preflight?.registerRule({
    id: 'typography-policy-violation', severity: 'error', message: t('Typography Pro settings are not allowed for this product.'),
    matches: (item) => {
      const typed = item as TypographyDesignItem;
      if (!hasPro(typed)) return false;
      const policy = typographyPolicy.value;
      if (!policy.enabled) return true;
      if (typed.typographyBadge) {
        const badge = normalizeBadge(typed.typographyBadge);
        const fonts = [badge.top.fontFamily, badge.bottom.fontFamily, ...(badge.center.enabled ? [badge.center.fontFamily] : [])];
        if (policy.allowedFonts.length && fonts.some((font) => !policy.allowedFonts.includes(font))) return true;
        const appearance = badge.appearance;
        return ((appearance.fill.type === 'linear-gradient' || appearance.fill.type === 'radial-gradient') && !policy.allowedEffects.includes('gradient'))
          || (appearance.stroke.enabled && !policy.allowedEffects.includes('outline'))
          || (appearance.shadow.enabled && !policy.allowedEffects.includes('shadow'))
          || (appearance.extrude.enabled && !policy.allowedEffects.includes('extrude'));
      }
      const config = normalizeTypography(typed.typography, String(typed.color ?? '#111827'));
      const font = String(typed.fontFamily ?? '');
      return !policy.allowedLayouts.includes(config.layout.mode)
        || config.layout.autoFit.maxChars > policy.maxChars
        || (policy.allowedFonts.length > 0 && !policy.allowedFonts.includes(font))
        || ((config.appearance.fill.type === 'linear-gradient' || config.appearance.fill.type === 'radial-gradient') && !policy.allowedEffects.includes('gradient'))
        || (config.appearance.stroke.enabled && !policy.allowedEffects.includes('outline'))
        || (config.appearance.shadow.enabled && !policy.allowedEffects.includes('shadow'))
        || (config.appearance.extrude.enabled && !policy.allowedEffects.includes('extrude'));
    },
  });
  const hydrateConfig = (payload: unknown) => {
    const config = (payload as { config?: { items?: { typographyPro?: unknown }; typographyPresets?: unknown; brandPalette?: unknown; settings?: Record<string, unknown> } })?.config;
    setTypographyPolicy(config?.items?.typographyPro);
    setTypographyPresets(config?.typographyPresets);
    setTypographyBrandPalette(config?.brandPalette, config?.settings?.color_picker_mode);
    unregisterBadgeLibrary?.();
    unregisterBadgeLibrary = null;
    if (typographyPolicy.value.enabled) {
      unregisterBadgeLibrary = Veyop.designer?.registerLeftPanel({ id: 'typography-badges', group: 'text', label: t('Badges'), description: t('Create print-ready text badges'), icon: 'M12 3l2.8 5.7L21 9.6l-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2 7.5 14 3 9.6l6.2-.9L12 3Z', render: () => ({ component: BadgeLibraryPanel }), priority: 55 }) ?? null;
    }
  };
  if (Veyop.hooks?.whenReady) Veyop.hooks.whenReady('designer', hydrateConfig);
  else Veyop.hooks?.on('veyop:designer:ready', hydrateConfig);
}

function boot(): void {
  ensureDesignerStyles();
  const tryRegister = () => { if (window.Veyop) register(window.Veyop); };
  tryRegister();
  const start = Date.now();
  const timer = window.setInterval(() => {
    tryRegister();
    if (registered || Date.now() - start > 5000) window.clearInterval(timer);
  }, 50);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
